export type PgnToken =
  | { type: "number"; value: number }
  | { type: "san"; value: string }
  | { type: "comment"; value: string }
  | { type: "nag"; value: number }
  | { type: "lparen" }
  | { type: "rparen" }
  | { type: "result"; value: string };

export type PgnTreeMove = {
  san: string;
  comment: string;
  nags: number[];
  alternatives: PgnTreeMove[][];
};

const RESULT_RE = /^(?:1-0|0-1|1\/2-1\/2|\*)$/;

export function looksLikeSan(raw: string): boolean {
  const token = raw.replace(/0-0-0/g, "O-O-O").replace(/0-0/g, "O-O");
  const core = token.replace(/[+#]+$/g, "").replace(/=[NBRQ]$/i, "");
  if (core === "O-O" || core === "O-O-O") return true;
  return (
    /^[NBRQK]x[a-h][1-8]$/.test(core) ||
    /^[NBRQK][a-h]x[a-h][1-8]$/.test(core) ||
    /^[NBRQK][1-8]x[a-h][1-8]$/.test(core) ||
    /^[NBRQK][a-h][a-h][1-8]$/.test(core) ||
    /^[NBRQK][1-8][a-h][1-8]$/.test(core) ||
    /^[NBRQK][a-h][1-8]$/.test(core) ||
    /^[a-h]x[a-h][1-8]$/.test(core) ||
    /^[a-h][1-8]$/.test(core)
  );
}

export function splitPgn(text: string): { headers: Record<string, string>; movetext: string } {
  const headers: Record<string, string> = {};
  const headerRe = /\[(\w+)\s+"([^"]*)"\]/g;
  let match: RegExpExecArray | null;
  let lastHeaderEnd = 0;
  while ((match = headerRe.exec(text))) {
    headers[match[1]] = match[2];
    lastHeaderEnd = match.index + match[0].length;
  }
  const movetext = text.slice(lastHeaderEnd).trim();
  return { headers, movetext };
}

export function tokenizeMovetext(movetext: string): PgnToken[] {
  const tokens: PgnToken[] = [];
  let i = 0;
  const s = movetext.replace(/\r\n/g, "\n");
  while (i < s.length) {
    const ch = s[i];
    if (/\s/.test(ch)) {
      i += 1;
      continue;
    }
    if (ch === "{") {
      const end = s.indexOf("}", i + 1);
      if (end < 0) throw new Error("Unclosed PGN comment");
      tokens.push({ type: "comment", value: s.slice(i + 1, end).trim() });
      i = end + 1;
      continue;
    }
    if (ch === ";") {
      const end = s.indexOf("\n", i + 1);
      const line = end < 0 ? s.slice(i + 1) : s.slice(i + 1, end);
      tokens.push({ type: "comment", value: line.trim() });
      i = end < 0 ? s.length : end;
      continue;
    }
    if (ch === "(") {
      tokens.push({ type: "lparen" });
      i += 1;
      continue;
    }
    if (ch === ")") {
      tokens.push({ type: "rparen" });
      i += 1;
      continue;
    }
    if (ch === "$") {
      let j = i + 1;
      while (j < s.length && /\d/.test(s[j])) j += 1;
      tokens.push({ type: "nag", value: Number(s.slice(i + 1, j) || "0") });
      i = j;
      continue;
    }
    let j = i;
    while (
      j < s.length &&
      !/\s/.test(s[j]) &&
      s[j] !== "(" &&
      s[j] !== ")" &&
      s[j] !== "{" &&
      s[j] !== ";"
    ) {
      j += 1;
    }
    let raw = s.slice(i, j);
    i = j;
    if (!raw) continue;
    if (/^\d+\.+$/.test(raw)) {
      tokens.push({ type: "number", value: Number(raw.replace(/\.+$/, "")) });
      continue;
    }
    if (RESULT_RE.test(raw)) {
      tokens.push({ type: "result", value: raw });
      continue;
    }
    raw = raw.replace(/^[0-9]+\.+/, "");
    if (!raw) continue;
    if (RESULT_RE.test(raw)) {
      tokens.push({ type: "result", value: raw });
      continue;
    }
    const san = raw.replace(/0-0-0/g, "O-O-O").replace(/0-0/g, "O-O");
    if (!looksLikeSan(san)) {
      throw new Error(`Unrecognized PGN token: ${raw}`);
    }
    tokens.push({ type: "san", value: san });
  }
  return tokens;
}

export function parseMoveTree(tokens: PgnToken[]): PgnTreeMove[] {
  let i = 0;

  function parseSequence(): PgnTreeMove[] {
    const moves: PgnTreeMove[] = [];
    while (i < tokens.length) {
      const tok = tokens[i];
      if (tok.type === "rparen" || tok.type === "result") break;
      if (tok.type === "number") {
        i += 1;
        continue;
      }
      if (tok.type === "lparen") {
        i += 1;
        const variation = parseSequence();
        if (tokens[i]?.type !== "rparen") throw new Error("Unclosed PGN variation");
        i += 1;
        if (!moves.length) throw new Error("Variation with no preceding move");
        moves[moves.length - 1].alternatives.push(variation);
        continue;
      }
      if (tok.type === "comment") {
        if (moves.length) {
          const prev = moves[moves.length - 1];
          prev.comment = prev.comment ? `${prev.comment} ${tok.value}` : tok.value;
        }
        i += 1;
        continue;
      }
      if (tok.type === "nag") {
        if (moves.length) moves[moves.length - 1].nags.push(tok.value);
        i += 1;
        continue;
      }
      if (tok.type === "san") {
        const node: PgnTreeMove = { san: tok.value, comment: "", nags: [], alternatives: [] };
        i += 1;
        while (i < tokens.length && (tokens[i].type === "comment" || tokens[i].type === "nag")) {
          const extra = tokens[i];
          if (extra.type === "comment") {
            node.comment = node.comment ? `${node.comment} ${extra.value}` : extra.value;
          } else if (extra.type === "nag") {
            node.nags.push(extra.value);
          }
          i += 1;
        }
        moves.push(node);
        continue;
      }
      i += 1;
    }
    return moves;
  }

  return parseSequence();
}

export function parsePgnTree(text: string): {
  headers: Record<string, string>;
  moves: PgnTreeMove[];
} {
  const { headers, movetext } = splitPgn(text);
  return { headers, moves: parseMoveTree(tokenizeMovetext(movetext)) };
}
